import { PureComponent } from "react";
import { connect } from "react-redux";
import withRouter from "withRouter";
import { createStructuredSelector } from "reselect";
import { mapIsEmpty } from "utils/helpers";
import Table from "components/Table";
import Button from "components/Button";
import PopoverLinklist from "components/Popover/PopoverLinklist";
import H1 from "components/H1";
import Icon from "components/Icon";
import Tooltip from "components/Tooltip";
import {
  Wrapper,
  Title,
  StyledEmptyList,
  EmptyText,
  HeaderFilter,
  RowHyperlink,
  ButtonWrapper,
} from "./styledComponents";
import { ButtonLink } from "components/Button/ButtonLink";
import UpgradeCta from "containers/UpgradeCta";
import Filter from "./Filter";
import ListError from "components/ListError";
import Spinner from "components/Spinner";
import {
  selectUser,
  selectAllLeads,
  selectFetching,
  selectPagingObject,
  selectListError,
  selectLoaded,
  selectUpgradeRequired,
} from "../selectors";
import { leadsLockedForUser } from "../leadAccess";
import { fetchLeadsRequest, downLoadLeadRequest } from "../actions";
import dayjs from "dayjs";
import { withUseFormHook } from "utils/hooks";

const columns = (userType) => {
  const allowed = ["pro", "enterprise_admin", "super_admin"].includes(userType);
  return [
    {
      header: "first",
      path: "first_name",
      type: "name",
      width: "100px",
      customRow: (rowData) => {
        const { first_name } = rowData;
        
        const shouldTruncate = first_name.length > 12;
        return shouldTruncate ? (
          <Tooltip
            text={first_name}
            weight="normal"
            size="14px"
            width="150px"
            casing="none"
            position="bottom"
          >
            {`${first_name.slice(0, 12)}...`}
          </Tooltip>
        ) : (
          `${first_name}`
        );
      },
    },
    {
      header: "last",
      path: "last_name",
      type: "name",
      width: "100px",
      customRow: (rowData) => {
        const { last_name } = rowData;
        
        const shouldTruncate = last_name.length > 12;
        return shouldTruncate ? (
          <Tooltip
            text={last_name}
            weight="normal"
            size="14px"
            width="150px"
            casing="none"
            position="bottom"
          >
            {`${last_name.slice(0, 12)}...`}
          </Tooltip>
        ) : (
          `${last_name}`
        );
      },
    },
    {
      header: "email",
      path: "email",
      width: "200px",
      type: "email",
      customRow: (rowData) => {
        const shouldTruncate = rowData.email.length > 25;
        return (
          <RowHyperlink href={`mailto:${rowData.email}`}>
            {" "}
            {shouldTruncate ? (
              <Tooltip
                text={rowData.email}
                weight="normal"
                size="14px"
                width="250px"
                casing="none"
                position="bottom"
              >
                {` ${rowData.email.slice(0, 25)}...`}
              </Tooltip>
            ) : (
              rowData.email
            )}
          </RowHyperlink>
        );
      },
    },
    { width: "120px", header: "phone", path: "phone", type: "phone" },
    {
      width: "125px",
      header: "twitter",
      path: "twitter",
      type: "twitter",
      customRow: (rowData) => {
        let shouldTruncate = rowData.twitter.length > 20;

        return (
          <RowHyperlink
            onClick={() =>
              window.open(`https://twitter.com/${rowData.twitter}`)
            }
          >
            {shouldTruncate ? (
              <Tooltip
                text={`${rowData.twitter}`}
                weight="normal"
                size="14px"
                width="250px"
                casing="none"
                position="bottom"
              >
                {`@${rowData.twitter.slice(0, 20)}...`}
              </Tooltip>
            ) : rowData.twitter ? (
              `@${rowData.twitter}`
            ) : (
              ""
            )}
          </RowHyperlink>
        );
      },
    },
    allowed && {
      width: "110px",
      header: "dob",
      path: "birthday",
      type: "birthday",
    },
    allowed && { width: "100px", header: "zip", path: "zipcode", type: "zip" },
    { width: "110px", header: "last seen", path: "time_shared", type: "date" },
    {
      width: "60px",
      header: "",
      type: "dropdown",
      customRow: (rowData) => (
        <PopoverLinklist links={PopoverLinks(rowData.channel.id)} />
      ),
    },
  ];
};
// Relative, so the link opens on whichever host served the dashboard. A
// hard-coded host sent production customers to staging. `/ch/:slug` resolves a
// numeric channel id too (the leads payload carries only `channel.id`).
export const PopoverLinks = (id) => {
  const url = `/ch/${id}`;
  const items = [
    {
      title: "View Gallery",
      onClick: () => window.open(url),
    },
  ];
  return items;
};

const EmptyList = () => (
  <StyledEmptyList>
    <Icon className="channel-list__empty-icon" size="140" name="channels" />
    <EmptyText>It's looking empty around here…</EmptyText>
  </StyledEmptyList>
);

/**
 * Lower bound used when the user picks an end date but no start. Matches the
 * gallery's `DEFAULT_OLDEST_DATE` — comfortably before any lead exists.
 */
const OPEN_RANGE_OLDEST = "2000-01-01";

const asValidDay = (value) => {
  if (value == null || value === "") return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

/**
 * Builds the `start_date`/`end_date` pair for the leads list and CSV export.
 *
 * Two things make this fiddly, and they pull in opposite directions.
 *
 * First, a half-chosen range is genuinely reachable: the datepicker's
 * onChange shape is `{ startDate, endDate }` and an unchosen bound is `null`.
 * Apply submits whatever is in the form. The old code guarded only on the
 * range object, so the unset side went through `dayjs(null).format()` —
 * which returns the literal string `"Invalid Date"` — and that word was sent
 * as the request's `end_date`.
 *
 * Second, an unset side cannot simply be dropped. Both consumers apply the
 * filter only when BOTH bounds are present (`api/v2/leads_controller.rb:46`
 * and `api/v1/leads_controller.rb:40`, each `if params[:start_date] &&
 * params[:end_date]`, feeding `Lead.by_time_shared` →
 * `where(time_shared: start..end)`). Omitting one bound would therefore skip
 * date filtering altogether and quietly return EVERY lead — a wider, quieter
 * wrong answer than the one being fixed.
 *
 * So a missing bound is WIDENED to an explicit one rather than dropped, which
 * is what actually makes "from this date onward" true. A fully unset range
 * still sends nulls, because then no filtering is what the user asked for.
 */
const buildDateFilter = (dateRange) => {
  const start = asValidDay(dateRange && dateRange.startDate);
  const end = asValidDay(dateRange && dateRange.endDate);

  if (!start && !end) return { start_date: null, end_date: null };

  return {
    // Formatting for the both-present case is deliberately unchanged; only a
    // synthesised bound is normalised, and the end to the close of its day so
    // leads shared later that day are not silently excluded.
    start_date: (start || dayjs(OPEN_RANGE_OLDEST).startOf("day")).format(),
    end_date: (end || dayjs().endOf("day")).format(),
  };
};

class LeadsList extends PureComponent {
  state = {
    sort: {},
    page: 1,
  };

  componentDidMount() {
    if (this.props.user.permission_role !== "") {
      this.refetch();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.user.permission_role === "" &&
      prevProps.user.permission_role !== this.props.user.permission_role
    ) {
      this.refetch();
    }
  }

  // Every refetch — mount, filter, page, sort, retry — issues the request the
  // state ON SCREEN describes. Routing them all through one method is what
  // makes that true by construction, rather than by five call sites happening
  // to agree; they did not, which is the defect below.
  //
  // It is also the one place the plan gate lives: a plan known to lack lead
  // capture sends no leads request from ANY path. Rails that still answers
  // that denial with 401 would otherwise sign the user out (utils/request.js).
  refetch = (overrides) => {
    if (leadsLockedForUser(this.props.user)) return;
    this.props.fetchLeads({ ...this.getFilter(), ...overrides });
  };

  handleFilter = () => this.refetch();

  handleRetry = () => this.refetch();

  handlePageChange = (page) => this.refetch({ page });

  handleSortChanged = (sortObj) => {
    this.setState({ sort: sortObj }, () => this.refetch());
  };

  // The route's channel is the DEFAULT scope, not an override.
  //
  // This used to take a `channelId` argument, and only mount passed it — so on
  // /leads/:channelId the list was FETCHED scoped to the route channel while
  // paging and sorting called it bare and sent no `source_channel` at all,
  // silently widening to every lead on the account. The two disagreed about
  // which channel the user was looking at, and the table gave no sign of it.
  //
  // Reading `params` here instead removes the argument, so no call site can
  // disagree. Precedence is `source_channel || channelId`, not the reverse: an
  // explicit filter wins, and the route supplies the scope only while none is
  // set. (The Filter does eventually seed `source_channel` from the route, via
  // a channel-detail fetch — so the old bug was a race that closed once that
  // response landed, and stayed open for good if it failed.)
  //
  // One asymmetry is deliberate and worth naming: react-hook-form reports
  // "all my channels" and "nothing chosen" identically, both empty, so on a
  // channel-scoped route an explicit "all" reads as unset and falls back to the
  // route's channel. Telling them apart needs a sentinel in the Filter form,
  // which is a separate change.
  getFilter = () => {
    const { date_range, source_channel, search, params } = this.props;

    return {
      page: this.props.paging.currentPage,
      ...buildDateFilter(date_range),
      source_channel: source_channel || params.channelId,
      search_term: search,
      sort_name: this.state.sort.path,
      sort_direction: this.state.sort.isDesc ? "desc" : "asc",
    };
  };

  render() {
    const { isFetching, loaded, listError, upgradeRequired, user, leads, downloadLeads, paging, date_range, source_channel, search } =
      this.props;
    const { permission_role } = user;
    // Locked up front from the plan the session reports, or after the fact when
    // rails answers 403 feature_not_available (e.g. a role with no plan).
    const locked = leadsLockedForUser(user) || upgradeRequired;
    const isEmpty = mapIsEmpty(leads);
    // A failed fetch is empty too, which is how it used to render as "you have
    // no leads". Everything keyed on emptiness — including the Download CSV
    // button, which would export nothing — has to ask this first.
    const hasFailed = Boolean(listError);
    const showTable = !locked && !isFetching && !isEmpty && !hasFailed;
    const data = !isEmpty ? Object.keys(leads).map((k) => leads[k]) : null;
    const filterForm = {
      dateRange: date_range,
      sourceChannel: source_channel,
      search,
    }

    return (
      <Wrapper>
        <Title>
          <HeaderFilter>
            <H1>Leads</H1>
            {/* Nothing to filter while the plan lacks lead capture. */}
            {!locked && (
              <Filter
                formData={filterForm}
                filterSubmit={this.handleFilter}
                params={this.props.params}
                form={this.props.form}
              />
            )}
          </HeaderFilter>
          {showTable && (
            <ButtonWrapper>
              <Button onClick={() => downloadLeads(this.getFilter())}>
                Download CSV
              </Button>
            </ButtonWrapper>
          )}
        </Title>
        {locked ? (
          <div style={{ marginTop: "2em", color: "white" }}>
            <ButtonWrapper>
              <UpgradeCta as={ButtonLink}>
                Upgrade to PLUS for Leads
              </UpgradeCta>
            </ButtonWrapper>
          </div>
        ) : !loaded ? (
          // Keyed on `loaded` — has the request settled — not on `isFetching`,
          // which is equally false before the first fetch is dispatched and
          // after one returns. See ChannelList for the same fix and why the
          // empty state has to mean "the server said none" and nothing else.
          <Spinner />
        ) : hasFailed ? (
          // Before `isEmpty`: a failed fetch has no leads either, so asking
          // emptiness first is what routed a 500 into "It's looking empty
          // around here…".
          <ListError
            title="We couldn’t load your leads"
            onRetry={this.handleRetry}
            isRetrying={isFetching}
          />
        ) : isEmpty ? (
          <EmptyList />
        ) : (
          <Table
            columns={columns(permission_role).filter((x) => x)}
            data={data}
            isFetching={isFetching}
            {...paging}
            onPageChange={this.handlePageChange}
            sortChanged={this.handleSortChanged}
            isLeadsTable={true}
          />
        )}
      </Wrapper>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  user: selectUser(),
  isFetching: selectFetching(),
  loaded: selectLoaded(),
  listError: selectListError(),
  upgradeRequired: selectUpgradeRequired(),
  leads: selectAllLeads(),
  paging: selectPagingObject(),
});

const mapDispatchToProp = (dispatch) => ({
  fetchLeads: (params) => dispatch(fetchLeadsRequest(params)),
  downloadLeads: (params) => dispatch(downLoadLeadRequest(params)),
});

// withRouter outermost so `params` reaches the class — v7 injects no router
// props into a route `element`, and this component reads :channelId.
export default withRouter(
  connect(mapStateToProps, mapDispatchToProp)(withUseFormHook(LeadsList))
);
